/**
 * Crypto cashout address validators.
 *
 * Cauldron's crypto payouts are routed via Stripe Bridge, which only
 * supports EVM-family chains (Base, Ethereum, Polygon, Arbitrum, Optimism)
 * + Solana. XRPL classic addresses (`r…`) look superficially plausible but
 * Bridge cannot send to them — pasting one results in a confusing
 * sanctions-screen / payout failure several minutes later.
 *
 * We catch the mistake at the form layer with friendly copy so users can
 * fix it before submission.
 *
 * Returns:
 *   { ok: true }                                        — valid for the picked chain
 *   { ok: false, code: "xrpl",   message: "..." }       — XRPL address pasted
 *   { ok: false, code: "format", message: "..." }       — doesn't match chain shape
 *   { ok: false, code: "empty" }                        — nothing entered yet
 */

// XRPL classic: starts with `r`, base58, 25-35 chars (typical 33-34).
// Deliberately tight so we don't false-positive on, say, Solana addresses
// that happen to start with `r` (Solana is 32-44 base58 chars but does NOT
// start with a single lowercase r in the way XRPL does — the first char
// for XRPL is always `r`, while Solana addresses are case-mixed and
// almost never start with `r` followed by the XRPL alphabet).
const XRPL_CLASSIC = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;

// EVM (Base / Ethereum / Polygon / Arbitrum / Optimism): 0x + 40 hex chars.
const EVM_ADDRESS = /^0x[a-fA-F0-9]{40}$/;

// Solana: 32-44 base58 chars (no leading r — see note above).
const SOLANA_ADDRESS = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

export function validateCryptoAddress(address, chain) {
  const value = String(address || "").trim();
  if (!value) return { ok: false, code: "empty" };

  // ALWAYS flag XRPL first — it's the most likely confused-paste because
  // users earn CAUL on XRPL and may grab their XRPL wallet by reflex.
  if (XRPL_CLASSIC.test(value)) {
    return {
      ok: false,
      code: "xrpl",
      message:
        "That looks like an XRPL address (CAUL's own chain). Stripe Bridge can't send USDC to XRPL — paste a Base / Ethereum / Polygon address (starts with 0x…) or a Solana address instead.",
    };
  }

  const c = String(chain || "").toLowerCase();
  if (["base", "ethereum", "polygon", "arbitrum", "optimism"].includes(c)) {
    if (!EVM_ADDRESS.test(value)) {
      return {
        ok: false,
        code: "format",
        message: `That doesn't look like a ${c} address. Expected: 0x followed by 40 hex characters.`,
      };
    }
    return { ok: true };
  }

  if (c === "solana") {
    if (!SOLANA_ADDRESS.test(value)) {
      return {
        ok: false,
        code: "format",
        message: "That doesn't look like a Solana address (32–44 base58 chars).",
      };
    }
    return { ok: true };
  }

  // Unknown chain — fall through as ok rather than blocking a future
  // chain we haven't taught the validator about yet.
  return { ok: true };
}

/**
 * Best-effort chain auto-detect from a pasted address. Used to flip the
 * chain selector to match what the user actually pasted so they can't pick
 * the wrong network by accident.
 *
 * Returns:
 *   { chain: "xrpl" }                 — XRPL classic (we don't support it for cashout)
 *   { chain: "evm" }                  — 0x… EVM-compatible
 *   { chain: "solana" }               — base58 32–44 chars
 *   { chain: null }                   — empty or unknown
 */
export function detectChainFromAddress(address) {
  const value = String(address || "").trim();
  if (!value) return { chain: null };
  if (XRPL_CLASSIC.test(value)) return { chain: "xrpl" };
  if (EVM_ADDRESS.test(value)) return { chain: "evm" };
  if (SOLANA_ADDRESS.test(value)) return { chain: "solana" };
  return { chain: null };
}
